import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../components/layout/Topbar.jsx";
import CompanySelector from "../components/ui/CompanySelector.jsx";
import { PriorityBadge, TicketStatusBadge, Pagination, Modal } from "../components/ui/index.jsx";
import {
  adminGetTickets,
  adminCreateTicket,
  adminDeleteTicket,
  adminUpdateTicket,
} from "../services/ticketService.js";
import { getCompanies } from "../services/companyService.js";

// ─────────────────────────────────────────────
//  CONSTANTES
// ─────────────────────────────────────────────

const STATUSES = [
  { value: "ALL",         label: "Tous"        },
  { value: "OPEN",        label: "Ouverts"     },
  { value: "IN_PROGRESS", label: "En cours"    },
  { value: "PENDING",     label: "En attente"  },
  { value: "RESOLVED",    label: "Résolus"     },
  { value: "CLOSED",      label: "Fermés"      },
];

const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

// ── SLA — seuils par priorité (en heures écoulées depuis la création) ───────
// Interprétation du CDC (4.5.3) : "temps moyen de première réponse < 4h",
// "temps moyen de résolution < 24h selon priorité" — plus la priorité est
// haute, plus la fenêtre avant alerte est courte. Un ticket déjà résolu ou
// fermé n'est plus concerné par le SLA.
const SLA_THRESHOLDS = {
  CRITICAL: { warning: 2,  late: 4  },
  HIGH:     { warning: 4,  late: 8  },
  MEDIUM:   { warning: 12, late: 24 },
  LOW:      { warning: 24, late: 48 },
};
const SLA_OPEN_STATUSES = ["OPEN", "IN_PROGRESS", "PENDING"];

/**
 * Retourne "late" (retard rouge), "warning" (proche limite orange) ou null
 * (dans les temps / non concerné) pour un ticket donné.
 */
function getSlaState(ticket) {
  if (!SLA_OPEN_STATUSES.includes(ticket.status) || !ticket.createdAt) return null;
  const thresholds = SLA_THRESHOLDS[ticket.priority] || SLA_THRESHOLDS.LOW;
  const hoursElapsed = (Date.now() - new Date(ticket.createdAt).getTime()) / 3_600_000;
  if (hoursElapsed >= thresholds.late)    return "late";
  if (hoursElapsed >= thresholds.warning) return "warning";
  return null;
}

/** Petit point coloré indiquant l'état SLA — à placer devant le titre du ticket. */
function SlaDot({ ticket }) {
  const state = getSlaState(ticket);
  if (!state) return null;
  return (
    <span
      title={state === "late" ? "En retard SLA" : "Proche de la limite SLA"}
      style={{
        display: "inline-block", width: 8, height: 8, borderRadius: "50%",
        background: state === "late" ? "#EF4444" : "#F59E0B",
        marginRight: 7, flexShrink: 0,
      }}
    />
  );
}

// ─────────────────────────────────────────────
//  MODALE — Création de ticket
// ─────────────────────────────────────────────

function CreateTicketModal({ companies, onSave, onClose }) {
  const [form, setForm] = useState({
    title:       "",
    description: "",
    category:    "autre",
    priority:    "MEDIUM",
    companyId:   companies[0]?.id || "",
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setError("");
    if (!form.title.trim())   return setError("Le titre est obligatoire.");
    if (!form.companyId)      return setError("L'entreprise est obligatoire.");

    setLoading(true);
    try {
      await adminCreateTicket(form);
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Nouveau ticket"
      onClose={onClose}
      footer={<>
        <button className="btn-itm btn-itm-outline" onClick={onClose}>Annuler</button>
        <button className="btn-itm btn-itm-primary" onClick={handleSubmit} disabled={loading}>
          <i className="bi bi-send"/> {loading ? "Envoi…" : "Créer"}
        </button>
      </>}
    >
      {error && (
        <div style={{ background:"var(--danger-dim)", border:"1px solid var(--danger)", borderRadius:8, padding:"8px 12px", color:"var(--danger)", fontSize:13, marginBottom:12 }}>
          <i className="bi bi-exclamation-triangle me-2"/>{error}
        </div>
      )}

      {/* Entreprise */}
      <div className="form-group">
        <label className="itm-label">Entreprise *</label>
        <select className="itm-input" value={form.companyId} onChange={e => set("companyId", e.target.value)}>
          <option value="">— Choisir —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Titre */}
      <div className="form-group">
        <label className="itm-label">Titre *</label>
        <input className="itm-input" placeholder="Décrivez brièvement le problème"
          value={form.title} onChange={e => set("title", e.target.value)}/>
      </div>

      {/* Description */}
      <div className="form-group">
        <label className="itm-label">Description</label>
        <textarea className="itm-input" rows={3} style={{ resize:"vertical" }}
          placeholder="Depuis quand ? Quels appareils ? Messages d'erreur ?"
          value={form.description} onChange={e => set("description", e.target.value)}/>
      </div>

      <div className="form-row">
        {/* Catégorie */}
        <div className="form-group">
          <label className="itm-label">Catégorie</label>
          <select className="itm-select" style={{ width:"100%" }}
            value={form.category} onChange={e => set("category", e.target.value)}>
            {["reseau","poste","serveur","logiciel","securite","autre"].map(c => (
              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
        </div>

        {/* Priorité */}
        <div className="form-group">
          <label className="itm-label">Priorité</label>
          <select className="itm-select" style={{ width:"100%" }}
            value={form.priority} onChange={e => set("priority", e.target.value)}>
            <option value="LOW">Basse</option>
            <option value="MEDIUM">Moyenne</option>
            <option value="HIGH">Haute</option>
            <option value="CRITICAL">Critique</option>
          </select>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────

export default function TicketsPage() {
  const navigate = useNavigate();

  // ── Entreprise sélectionnée (null = toutes) ───────────────────────────────
  const [company,   setCompany]   = useState(null);

  // ── Données ───────────────────────────────────────────────────────────────
  const [tickets,   setTickets]   = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  // ── Filtres ───────────────────────────────────────────────────────────────
  const [filterStatus,   setFilterStatus]   = useState("ALL");
  const [filterPriority, setFilterPriority] = useState("ALL");
  // "ALL" = tout, "REAL" = vrais tickets uniquement, "ALERT" = alertes système uniquement (NAS, sauvegardes…)
  const [filterAlert,    setFilterAlert]    = useState("ALL");
  const [search,         setSearch]         = useState("");
  const [page,           setPage]           = useState(0);

  // ── Modales ───────────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);

  // Charge les entreprises au montage (pour le sélecteur + modale création)
  useEffect(() => {
    getCompanies()
      .then(res => setCompanies(res.data || []))
      .catch(() => {});
  }, []);

  // Recharge les tickets quand l'entreprise change
  useEffect(() => {
    fetchTickets();
  }, [company]);

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminGetTickets({
        companyId: company?.id || undefined,
      });
      setTickets(Array.isArray(res) ? res : res.data ?? []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Suppression
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer ce ticket ?")) return;
    try {
      await adminDeleteTicket(id);
      setTickets(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  // Changement de statut rapide depuis la liste
  const handleStatusChange = async (id, status, e) => {
    e.stopPropagation();
    try {
      await adminUpdateTicket(id, { status });
      setTickets(prev => prev.map(t => t.id === id ? { ...t, status } : t));
    } catch (err) {
      alert(err.message);
    }
  };

  // Filtrage côté client
  const filtered = useMemo(() => {
    return tickets.filter(t => {
      if (filterStatus   !== "ALL" && t.status   !== filterStatus)                         return false;
      if (filterPriority !== "ALL" && t.priority !== filterPriority)                       return false;
      if (filterAlert === "REAL"  && t.systemAlert)  return false;
      if (filterAlert === "ALERT" && !t.systemAlert) return false;
      if (search && !t.title?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [tickets, filterStatus, filterPriority, filterAlert, search]);

  // Compteurs par statut
  const counts = useMemo(() => {
    const c = { ALL: tickets.length };
    STATUSES.slice(1).forEach(s => {
      c[s.value] = tickets.filter(t => t.status === s.value).length;
    });
    return c;
  }, [tickets]);

  const activeCount = (counts.OPEN || 0) + (counts.IN_PROGRESS || 0);

  return (
    <>
      <Topbar title="Tickets" subtitle="Administration"/>
      <div className="page-body">

        {/* ── En-tête ── */}
        <div className="page-header">
          <div>
            <div className="page-title">Tickets <span>clients</span></div>
            <div className="page-sub">
              {activeCount} actif{activeCount !== 1 ? "s" : ""} · {tickets.length} au total
              {company && <span style={{ marginLeft:8, color:"var(--accent)" }}>— {company.name}</span>}
            </div>
          </div>
          <div className="page-actions">
            <button className="btn-itm btn-itm-primary" onClick={() => setShowCreate(true)}>
              <i className="bi bi-plus-lg"/> Nouveau ticket
            </button>
          </div>
        </div>

        {/* ── Sélecteur d'entreprise + filtres ── */}
        <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>

          {/* Sélecteur d'entreprise — "Toutes" si null */}
          <CompanySelector
            selectedId={company?.id || null}
            onChange={setCompany}
            placeholder="Toutes les entreprises"
          />

          {/* Priorité */}
          <select className="itm-input" style={{ width:"auto" }}
            value={filterPriority} onChange={e => { setFilterPriority(e.target.value); setPage(0); }}>
            <option value="ALL">Toutes les priorités</option>
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>

          {/* Vrais tickets / alertes système (NAS, sauvegardes…) */}
          <select className="itm-input" style={{ width:"auto" }}
            value={filterAlert} onChange={e => { setFilterAlert(e.target.value); setPage(0); }}>
            <option value="ALL">Tickets + alertes système</option>
            <option value="REAL">Tickets uniquement</option>
            <option value="ALERT">Alertes système uniquement</option>
          </select>

          {/* Recherche */}
          <div className="input-group-itm" style={{ width:200, marginLeft:"auto" }}>
            <i className="bi bi-search input-icon"/>
            <input className="itm-input" placeholder="Rechercher…" value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}/>
          </div>
        </div>

        {/* ── Filtres rapides par statut ── */}
        <div style={{ display:"flex", gap:6, marginBottom:18, flexWrap:"wrap" }}>
          {STATUSES.map(({ value, label }) => (
            <button key={value}
              onClick={() => { setFilterStatus(value); setPage(0); }}
              style={{
                padding:"5px 12px", borderRadius:20, border:"1px solid", cursor:"pointer",
                fontSize:12, transition:"all .15s", display:"flex", alignItems:"center", gap:5,
                borderColor: filterStatus===value ? "var(--accent)" : "var(--border)",
                background:  filterStatus===value ? "var(--accent-dim)" : "transparent",
                color:       filterStatus===value ? "var(--accent)" : "var(--text-2)",
                fontWeight:  filterStatus===value ? 600 : 400,
              }}>
              {label}
              <span style={{
                background: filterStatus===value ? "var(--accent)" : "var(--navy-soft)",
                color:      filterStatus===value ? "#fff" : "var(--text-3)",
                borderRadius:99, padding:"0 6px", fontSize:10, fontWeight:700,
              }}>
                {counts[value] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {/* ── Chargement ── */}
        {loading && (
          <div style={{ textAlign:"center", padding:60, color:"var(--text-3)" }}>
            <i className="bi bi-arrow-repeat" style={{ fontSize:24, display:"block", marginBottom:8 }}/>
            Chargement des tickets…
          </div>
        )}

        {/* ── Erreur ── */}
        {error && !loading && (
          <div style={{ background:"var(--danger-dim)", border:"1px solid var(--danger)", borderRadius:10, padding:14, color:"var(--danger)", marginBottom:14, display:"flex", alignItems:"center", gap:12 }}>
            <i className="bi bi-exclamation-triangle"/>{error}
            <button className="btn-itm btn-itm-outline" style={{ marginLeft:"auto" }} onClick={fetchTickets}>
              Réessayer
            </button>
          </div>
        )}

        {/* ── Tableau ── */}
        {!loading && !error && (
          <div className="itm-card anim-fade-up">
            <table className="itm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Titre</th>
                  <th>Entreprise</th>
                  <th>Catégorie</th>
                  <th>Priorité</th>
                  <th>Statut</th>
                  <th>Créé le</th>
                  <th/>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign:"center", padding:40, color:"var(--text-3)" }}>
                      Aucun ticket trouvé
                    </td>
                  </tr>
                ) : filtered.slice(page * 10, (page + 1) * 10).map(t => (
                  <tr key={t.id} style={{ cursor:"pointer" }}
                    onClick={() => navigate(`/tickets/${t.id}`)}>

                    {/* ID */}
                    <td style={{ fontSize:12, color:"var(--text-3)", fontFamily:"monospace" }}>
                      {t.ateraTicketId ? `#${t.ateraTicketId}` : `#${t.id?.substring(0,8)}`}
                    </td>

                    {/* Titre — précédé du point SLA si applicable, suivi du badge alerte système */}
                    <td className="cell-primary">
                      <SlaDot ticket={t}/>{t.title}
                      {t.systemAlert && (
                        <span style={{
                          marginLeft:8, display:"inline-flex", alignItems:"center", gap:4,
                          padding:"1px 8px", borderRadius:20, fontSize:10, fontWeight:600,
                          background:"var(--warning-dim, rgba(245,158,11,.12))", color:"var(--warning, #F59E0B)",
                        }} title="Alerte système générée automatiquement (monitoring)">
                          <i className="bi bi-hdd-network" style={{ fontSize:10 }}/> Alerte système
                        </span>
                      )}
                    </td>

                    {/* Entreprise */}
                    <td>
                      {t.company ? (
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <div style={{ width:22, height:22, borderRadius:6, background:"var(--accent-dim)", color:"var(--accent)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700 }}>
                            {t.company.name?.[0]}
                          </div>
                          <span style={{ fontSize:12, color:"var(--text-2)" }}>{t.company.name}</span>
                        </div>
                      ) : "—"}
                    </td>

                    {/* Catégorie */}
                    <td style={{ fontSize:12, color:"var(--text-3)" }}>{t.category || "—"}</td>

                    {/* Priorité */}
                    <td><PriorityBadge priority={t.priority}/></td>

                    {/* Statut — cliquable pour changement rapide */}
                    <td onClick={e => e.stopPropagation()}>
                      <select
                        value={t.status}
                        onChange={e => handleStatusChange(t.id, e.target.value, e)}
                        style={{ background:"transparent", border:"none", color:"var(--text-2)", fontSize:12, cursor:"pointer", outline:"none" }}
                        onClick={e => e.stopPropagation()}
                      >
                        {STATUSES.slice(1).map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>

                    {/* Date */}
                    <td style={{ color:"var(--text-3)", fontSize:12 }}>
                      {t.createdAt ? new Date(t.createdAt).toLocaleDateString("fr-FR") : "—"}
                    </td>

                    {/* Actions */}
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display:"flex", gap:4 }}>
                        <button className="btn-icon-sm"
                          onClick={() => navigate(`/tickets/${t.id}`)}
                          title="Voir le détail">
                          <i className="bi bi-eye"/>
                        </button>
                        <button className="btn-icon-sm"
                          onClick={e => handleDelete(t.id, e)}
                          title="Supprimer"
                          style={{ color:"var(--danger)" }}>
                          <i className="bi bi-trash"/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} total={filtered.length} pageSize={10} onChange={setPage}/>
          </div>
        )}

        {/* ── Modale création ── */}
        {showCreate && (
          <CreateTicketModal
            companies={companies}
            onSave={() => { setShowCreate(false); fetchTickets(); }}
            onClose={() => setShowCreate(false)}
          />
        )}
      </div>
    </>
  );
}